import React, { useContext, useState, useCallback, useRef } from 'react';
import { ResponsiveHelper } from '@fec/frontend/foundation/client/responsive-helper';
import { DATEFORMAT, translate } from '../searchHelpers';
import { searchContextStore } from '../search/SearchContextProvider';
import moment from 'moment';
import Button from '@fec/frontend/elements/buttons/button/button';
import RangeCalendar from '@fec/frontend/modules/inputs/rangecalendar/rangecalendar';
import classNames from 'classnames';

export const SearchSelect = ({ onchange, content, value, searchParams }) => {
  const { dateFrom, dateTo } = searchParams;
  const { options } = content;
  const { state: searchContext } = useContext(searchContextStore);
  const [rangeSelectOpen, setRangeSelectOpen] = useState(false);

  const [rangeError, setRangeError] = useState(false);
  const [showA11yMenu, setShowA11yMenu] = useState(false);

  const [fromDateInput, setFromDateInput] = useState(dateFrom);
  const [toDateInput, setToDateInput] = useState(dateTo);

  const [dateRange, setDateRange] = useState(
    dateFrom && dateTo ? [dateFrom, dateTo] : [],
  );
  const _t = translate.bind(
    null,
    searchContext.searchConfig,
    searchContext.translations,
  );

  const inputFromRef = useRef();

  const closeButtonRef = useCallback((button) => {
    if (button) {
      button.focus();
    }
  }, []);

  const handleChange = (event) => {
    event.preventDefault();

    // Open the range datepicker and delegate value change to datepicker
    if (
      event.target.value === 'range' ||
      event.target.value === 'changerange'
    ) {
      setRangeSelectOpen(true);
      return;
    }

    setDateRange([]);
    onchange({
      key: content.name,
      value: event.target.value,
      event: event,
    });
  };

  const handleA11yDateChange = (event) => {
    event.preventDefault();

    if (event.target.classList.contains('js-search-select-from')) {
      setFromDateInput(event.target.value);
    } else if (event.target.classList.contains('js-search-select-to')) {
      setToDateInput(event.target.value);
    }
  };

  const handleA11yDateSubmit = (event) => {
    event.preventDefault();

    let momentFromDate = moment(fromDateInput);
    let momentToDate = moment(toDateInput);

    if (
      momentFromDate.isValid() &&
      momentToDate.isValid() &&
      momentFromDate > momentToDate
    ) {
      setRangeError(true);
    } else if (momentFromDate.isValid() && momentToDate.isValid()) {
      setShowA11yMenu(false);
      setRangeError(false);

      onchange({
        key: content.name,
        value: {
          dateFrom: momentFromDate.format(DATEFORMAT),
          dateTo: momentToDate.format(DATEFORMAT),
          type: 'range',
        },
        event: null,
      });

      handleRangeSelection([
        momentFromDate.format(DATEFORMAT),
        momentToDate.format(DATEFORMAT),
      ]);
    } else {
      setRangeError(true);
    }
  };

  const handleA11yDateFocus = () => {
    setShowA11yMenu(true);
  };

  const handleClick = (event) => {
    const clickedOnRange = event.detail === 0 && event.target.value === 'range';
    const clickedOnOption = event.target.tagName === 'OPTION';
    const isChrome = navigator.userAgent.toLowerCase().indexOf('chrome') > -1;

    // fix onClick handling for Chrome
    if (clickedOnRange && (isChrome || clickedOnOption)) {
      setRangeSelectOpen(true);
    }
  };

  const handleCloseClick = () => {
    setRangeSelectOpen(false);
    setShowA11yMenu(false);
    setRangeError(false);
  };

  const handleRangeSelection = (dates) => {
    setDateRange(dates);
    // Propagate change to the parent component
    if (Array.isArray(dates) && dates.length === 2) {
      // close the range selection after selection of two valid dates
      setRangeSelectOpen(false);
      onchange({
        key: content.name,
        value: {
          dateFrom: moment(dates[0]).format(DATEFORMAT),
          dateTo: moment(dates[1]).format(DATEFORMAT),
          type: 'range',
        },
        event: null,
      });
    }
  };

  const generateLabel = (filter) => {
    let label =
      filter.shortLabel && ResponsiveHelper.isMobile()
        ? _t(filter.shortLabel)
        : _t(filter.label);

    if (
      dateRange.length === 2 &&
      filter.value === 'range' &&
      !rangeSelectOpen
    ) {
      return `${moment(dateRange[0]).format('DD.MM.YY')} - ${moment(
        dateRange[1],
      ).format('DD.MM.YY')}`;
    }

    return label;
  };

  return (
    <div
      className={classNames(
        'select-menu-container',
        'select-menu-container--' + content.name,
        { 'select-menu-container--range': dateRange.length === 2 },
      )}>
      <select
        className="select-menu select-menu--rubric-search"
        onChange={handleChange}
        onClick={handleClick}
        value={value}>
        {options.map((filter) => {
          // when a date range is selected, we show a second <option> so the user can change the range again
          if (dateRange.length === 0 && filter.value === 'changerange') {
            return null;
          }
          return (
            <option
              className="select-option"
              key={filter.value}
              value={filter.value}>
              {generateLabel(filter)}{' '}
            </option>
          );
        })}
      </select>
      {rangeSelectOpen && (
        <div
          aria-modal="true"
          role="dialog"
          className="modal-flyout modal-flyout--in-content modal-flyout--top-space modal-flyout--right-aligned modal-flyout--dynamic-width modal-flyout--open">
          <div
            className="modal-flyout__overlay"
            onClick={() => setRangeSelectOpen(false)}></div>
          <div className="modal-flyout__head">
            <div className="modal-flyout__title">{_t('choose-range')}</div>
            <div className="modal-flyout__buttons">
              <Button
                ref={closeButtonRef}
                className={'js-modal-flyout__close-button'}
                screenReaderText={'Zeitraum wählen Dialog schliessen'}
                onClick={handleCloseClick}
                svgIcon="svg-icon--close"
                styleModifier="button--icon-only button--no-spacing button--neutral button--secondary"
              />
            </div>
          </div>

          {/*
            The RangeCalendar is not accessible or usable by keyboard. So
            we provide (only on focus) an alternative way of interaction
          */}
          <div
            className={
              'modal-flyout__head ' + (showA11yMenu ? '' : 'h-offscreen')
            }>
            {/*
              In VoiceOver the onFocus / onBlur Event does not always work with
              date-input-fields. With button-input it does. So we put a button
              in the beginning to be sure the menu will surly be displayed.
            */}
            <input
              className="h-offscreen"
              type="button"
              value="Wählen sie hier ihren eigenen Zeitraum"
              onClick={() => inputFromRef.current.focus()}
              onFocus={handleA11yDateFocus}
            />
            <label>
              Von:
              <input
                ref={inputFromRef}
                className={'js-search-select-from'}
                type="date"
                onChange={handleA11yDateChange}
                onFocus={handleA11yDateFocus}
                max={moment(new Date()).format('YYYY-MM-DD')}
                value={moment(fromDateInput).format('YYYY-MM-DD')}></input>
            </label>
            <label>
              Bis:
              <input
                className={'js-search-select-to'}
                type="date"
                onChange={handleA11yDateChange}
                onFocus={handleA11yDateFocus}
                max={moment(new Date()).format('YYYY-MM-DD')}
                value={moment(toDateInput).format('YYYY-MM-DD')}></input>
            </label>
            <input
              type="button"
              value="suchen"
              onClick={handleA11yDateSubmit}
              onFocus={handleA11yDateFocus}
            />
          </div>
          {rangeError && (
            <div
              className="modal-flyout__head form__validation form__validation--error"
              aria-live="polite">
              <p>{_t('range-error')}</p>
            </div>
          )}
          <div className="modal-flyout__content">
            <RangeCalendar
              onChange={handleRangeSelection}
              value={dateRange}
              businessUnit={searchContext.searchConfig.businessUnit}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
